import React, { useState } from "react";
import styled from "styled-components";
import { AlertColor } from "@mui/material/Alert";
import { Alert } from "../../../UI/Alert";
import { Button } from "../../../UI/MUIButton";
import { Paper } from "../../../UI/Paper";
import { Typography } from "../../../UI/Typography";
import useAuth from "../../login/AuthContext";
import {
  ProfilePhotoJob,
  ProfilePhotoJobStart,
  startProfilePhotoJob,
} from "../../../services/ProfilePhotoApi";

const OUTCOMES: Record<ProfilePhotoJobStart, { severity: AlertColor; message: string }> = {
  STARTED: {
    severity: "success",
    message: "Started. The API logs a summary when it finishes.",
  },
  ALREADY_RUNNING: {
    severity: "warning",
    message: "A backfill or cleanup is already running. Try again once it has finished.",
  },
  FAILED: {
    severity: "error",
    message: "The job could not be started.",
  },
};

const StyledPaper = styled(Paper)`
  padding: 16px;
  height: 100%;
`;

const Description = styled(Typography)`
  margin: 8px 0 16px !important;
`;

const Outcome = styled(Alert)`
  margin-top: 16px;
`;

interface ProfilePhotoJobCardProps {
  job: ProfilePhotoJob;
  title: string;
  description: string;
  buttonLabel: string;
  /** Asked before starting; the job only starts if the admin agrees. */
  confirmation?: string;
}

export const ProfilePhotoJobCard: React.FunctionComponent<ProfilePhotoJobCardProps> = ({
  job,
  title,
  description,
  buttonLabel,
  confirmation,
}) => {
  const { getAuthToken } = useAuth();
  const [isStarting, setIsStarting] = useState(false);
  const [outcome, setOutcome] = useState<ProfilePhotoJobStart>();

  const handleClick = async () => {
    if (confirmation && !window.confirm(confirmation)) {
      return;
    }

    setIsStarting(true);
    setOutcome(await startProfilePhotoJob(job, await getAuthToken()));
    setIsStarting(false);
  };

  return (
    <StyledPaper>
      <Typography variant="h6">{title}</Typography>
      <Description variant="body2">{description}</Description>
      <Button color="primary" variant="contained" disabled={isStarting} onClick={handleClick}>
        {buttonLabel}
      </Button>
      {outcome && (
        <Outcome severity={OUTCOMES[outcome].severity}>{OUTCOMES[outcome].message}</Outcome>
      )}
    </StyledPaper>
  );
};
